import { useState } from 'react';
import { Check, Pencil, X } from 'lucide-react';
import { Button, Input, Spinner, TableCell } from '@monksflow/monks-ui';
import { useUpdateChatSession } from '../../../model/useUpdateChatSession';
import { notificationService } from '@/lib/services/notification.service';
import type { SessionsTitleCellProps } from './SessionsTitleCell.types';
import { useTranslation } from 'react-i18next';

export function SessionsTitleCell({ contextId, title }: SessionsTitleCellProps) {
  const { t } = useTranslation('chats');

  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(title);

  const { mutateAsync: updateSession, isPending } = useUpdateChatSession();

  const handleEdit = (e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent) => {
    e.stopPropagation();
    setIsEditing(true);
    setEditValue(title);
  };

  const handleSave = async (e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent) => {
    e.stopPropagation();
    if (editValue.trim()) {
      try {
        await updateSession({ contextId, title: editValue.trim() });
        setIsEditing(false);
      } catch {
        notificationService.showError(t('sessions.sessionsTitleCell.failedToSaveTitle'), {
          persist: false,
          autoHideDuration: 3000,
        });
      }
    }
  };

  const handleCancel = (e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent) => {
    e.stopPropagation();
    setEditValue(title);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      void handleSave(e);
    } else if (e.key === 'Escape') {
      handleCancel(e);
    }
  };

  const handleContainerClick = (e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
  };

  return (
    <TableCell>
      {isEditing ? (
        <div className="flex items-center gap-2">
          <Input
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onClick={handleContainerClick}
            onKeyDown={handleKeyDown}
            className="flex-1"
          />
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => void handleSave(e)}
            disabled={!editValue.trim() || isPending}
            aria-label={t('sessions.sessionsTitleCell.save')}
          >
            {isPending ? <Spinner /> : <Check className="size-4" />}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleCancel}
            disabled={isPending}
            aria-label={t('sessions.sessionsTitleCell.cancel')}
          >
            <X className="size-4" />
          </Button>
        </div>
      ) : (
        <div className="group flex items-center gap-2 justify-between">
          <span className="text-sm text-foreground">{title}</span>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleEdit}
            className="opacity-0 group-hover:opacity-100"
            aria-label={t('sessions.sessionsTitleCell.edit')}
          >
            <Pencil className="size-4" />
          </Button>
        </div>
      )}
    </TableCell>
  );
}
